import { useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { FIELD_TYPES, OPTION_HINT, labelOf, shownName, type AvailableField, type BuilderField, type FieldType } from './builderApi';

/** All rows on one page — the grids are short configuration lists (the prototype scrolls, never pages). */
const ALL_ROWS = 1000;
const RESERVED = ['AND', 'OR', 'NOT'];
const SYMBOL_RE = /^[A-Za-z][A-Za-z0-9_]{0,11}$/;

interface Props {
  fields: BuilderField[];
  available: AvailableField[];
  readOnly: boolean;
  onChange: (fields: BuilderField[]) => void;
  onOpenFormula: (index: number, kind: 'calc' | 'accept') => void;
}

/** Word-boundary match of a symbol inside a formula (case-insensitive, as the parser). */
const symRe = (sym: string) => new RegExp(`\\b${sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');

/**
 * Field Configuration (prototype ALPACA_Daily_QC_Setup_v20.html): Available fields (+ to add)
 * and the Selected fields with Name · Caption · Type · Options · Default · Width · Freeze ·
 * Symbol · Calc (ƒx) · Accept (✓) · Move (↑↓). Edits change the page's draft only — nothing is
 * written until Save; the API validates the whole configuration then.
 */
export default function BuilderFieldConfig({ fields, available, readOnly, onChange, onOpenFormula }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [aSearch, setASearch] = useState('');
  const [sSearch, setSSearch] = useState('');

  // A field counts as taken under its storage column AND the name it is shown as.
  const taken = useMemo(() => new Set(fields.flatMap((f) => [f.name, shownName(f)].map((n) => n.toLowerCase()))), [fields]);
  const avail = useMemo(() => available.filter((a) => !taken.has(a.name.toLowerCase())), [available, taken]);
  const availShown = useMemo(() => {
    const q = aSearch.trim().toLowerCase();
    return q ? avail.filter((a) => `${a.name} ${a.type}`.toLowerCase().includes(q)) : avail;
  }, [avail, aSearch]);
  const selShown = useMemo(() => {
    const q = sSearch.trim().toLowerCase();
    const rows = fields.map((f, i) => ({ f, i }));
    return q ? rows.filter(({ f }) => [f.name, shownName(f), f.caption, f.symbol, f.type].join(' ').toLowerCase().includes(q)) : rows;
  }, [fields, sSearch]);

  const patch = (i: number, p: Partial<BuilderField>) => onChange(fields.map((f, j) => (j === i ? { ...f, ...p } : f)));

  const add = (a: AvailableField) => onChange([...fields, {
    oid: null, name: a.name, caption: a.name, type: a.type, options: null, default: null, width: 100,
    freeze: false, symbol: null, calc: null, accept: null, storable: a.storable,
  }]);

  /** Formulas (other than field `skip`'s) that use `sym`. */
  const usersOf = (sym: string, skip: number) => {
    const out: string[] = [];
    fields.forEach((x, j) => {
      if (j === skip) return;
      if (x.calc && symRe(sym).test(x.calc)) out.push(`${labelOf(x)} (calc)`);
      if (x.accept && symRe(sym).test(x.accept)) out.push(`${labelOf(x)} (accept)`);
    });
    return out;
  };

  const remove = async (i: number) => {
    const f = fields[i]!;
    const users = f.symbol ? usersOf(f.symbol, i) : [];
    if (users.length > 0 && !(await confirm({
      title: 'Remove field', tone: 'danger',
      message: `Symbol ${f.symbol} is used by: ${users.join(', ')}.\nRemove "${labelOf(f)}" anyway? Those formulas will show as invalid.`,
    }))) return;
    onChange(fields.filter((_, j) => j !== i));
  };

  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  /** The prototype's symbol rules: clear (if unused) · validate · carry a rename into every formula. */
  const commitSymbol = (i: number, raw: string) => {
    const f = fields[i]!;
    const old = f.symbol?.trim() ?? '';
    const val = raw.trim();
    if (val === old) return;
    if (!val) {
      const users = usersOf(old, -1);
      if (users.length) { notify.warning(`${old} is used in ${users.join(', ')}. Remove those references first.`); return; }
      patch(i, { symbol: null, calc: null, accept: null });
      return;
    }
    if (!SYMBOL_RE.test(val)) { notify.warning('Symbols start with a letter and use only letters, numbers, or _ (max 12).'); return; }
    if (RESERVED.includes(val.toUpperCase())) { notify.warning(`${val} is a reserved word.`); return; }
    const dup = fields.findIndex((x, j) => j !== i && (x.symbol ?? '').toUpperCase() === val.toUpperCase());
    if (dup >= 0) { notify.warning(`${val} is already assigned to ${labelOf(fields[dup]!)}.`); return; }
    let renamed = 0;
    const next = fields.map((x, j) => {
      let { calc, accept } = x;
      if (old) {
        if (calc && symRe(old).test(calc)) { calc = calc.replace(symRe(old), val); renamed++; }
        if (accept && symRe(old).test(accept)) { accept = accept.replace(symRe(old), val); renamed++; }
      }
      return j === i ? { ...x, symbol: val, calc, accept } : { ...x, calc, accept };
    });
    onChange(next);
    if (renamed) notify.info(`Renamed ${old} → ${val} in ${renamed} formula(s).`);
  };

  const availCols: AppColumn<AvailableField>[] = [
    { field: 'add', header: '', width: 34, sortable: false, filterable: false, noExport: true,
      render: (a) => (<button type="button" className="qcb-circle plus" title="Add field" aria-label={`Add ${a.name}`}
        disabled={readOnly} onClick={() => add(a)}><i className="ti ti-circle-plus" aria-hidden="true" /></button>) },
    { field: 'name', header: 'Name', width: 130, text: (a) => a.name,
      render: (a) => (<span title={a.storable ? a.name : `${a.name} — not a Daily QC column: its values are not saved`}>
        {a.name}{!a.storable && <span className="qcb-warn"> ⚠</span>}</span>) },
    { field: 'type', header: 'Type', width: 90, text: (a) => a.type },
  ];

  type Row = { f: BuilderField; i: number };
  const inp = (i: number, key: keyof BuilderField, value: string | null, extra: Record<string, unknown> = {}) => (
    <input className="qcb-cell" value={value ?? ''} disabled={readOnly || (extra.disabled as boolean)}
      placeholder={extra.placeholder as string | undefined} maxLength={extra.maxLength as number | undefined}
      onChange={(e) => patch(i, { [key]: e.target.value } as Partial<BuilderField>)} />
  );

  const selCols: AppColumn<Row>[] = [
    { field: 'del', header: '', width: 34, sortable: false, filterable: false, noExport: true,
      render: ({ i, f }) => (<button type="button" className="qcb-circle minus" title="Remove field" aria-label={`Remove ${f.name}`}
        disabled={readOnly} onClick={() => void remove(i)}><i className="ti ti-circle-minus" aria-hidden="true" /></button>) },
    { field: 'name', header: 'Name', width: 100, sortable: false, text: ({ f }) => shownName(f),
      render: ({ f }) => {
        const shown = shownName(f);
        const where = shown !== f.name ? ` — stored in ${f.name}` : '';
        return (<span title={f.storable ? `${shown}${where}` : `${shown} — not a Daily QC column: its values are not saved`}>
          {shown}{!f.storable && <span className="qcb-warn"> ⚠</span>}</span>);
      } },
    { field: 'caption', header: 'Caption', width: 120, sortable: false, filterable: false, text: ({ f }) => f.caption ?? '',
      render: ({ f, i }) => inp(i, 'caption', f.caption, { maxLength: 100 }) },
    { field: 'type', header: 'Type', width: 96, sortable: false, filterable: false, text: ({ f }) => f.type,
      render: ({ f, i }) => (
        <select className="qcb-cell" value={f.type} disabled={readOnly}
          onChange={(e) => patch(i, { type: e.target.value as FieldType })}>
          {FIELD_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>) },
    { field: 'options', header: <span title="ComboBox: choices (comma or | separated) · Lookup: TemplateID:Field · AutoNumber: prefix">Options</span>,
      headerText: 'Options', width: 110, sortable: false, filterable: false, text: ({ f }) => f.options ?? '',
      render: ({ f, i }) => {
        const hint = OPTION_HINT[f.type];
        return (<span title={hint?.title}>
          {inp(i, 'options', f.options, { disabled: !hint, placeholder: hint?.placeholder ?? '', maxLength: 1000 })}</span>);
      } },
    { field: 'default', header: <span title="Pre-filled value on new rows. Use 'today' for dates.">Default</span>, headerText: 'Default',
      width: 90, sortable: false, filterable: false, text: ({ f }) => f.default ?? '',
      render: ({ f, i }) => inp(i, 'default', f.default, {
        disabled: !!f.calc || f.type === 'Attachment' || f.type === 'AutoNumber', placeholder: f.type === 'Date' ? 'today' : '', maxLength: 100,
      }) },
    { field: 'width', header: 'Width', width: 68, sortable: false, filterable: false, align: 'center', text: ({ f }) => String(f.width),
      render: ({ f, i }) => (
        <input className="qcb-cell qcb-num" type="number" min={40} max={400} value={f.width} disabled={readOnly}
          onChange={(e) => patch(i, { width: Number.parseInt(e.target.value, 10) || 0 })}
          onBlur={(e) => patch(i, { width: Math.max(40, Math.min(400, Number.parseInt(e.target.value, 10) || 100)) })} />) },
    { field: 'freeze', header: 'Freeze', width: 62, sortable: false, filterable: false, align: 'center', text: ({ f }) => (f.freeze ? 'Yes' : 'No'),
      render: ({ f, i }) => (<input type="checkbox" checked={f.freeze} disabled={readOnly} aria-label={`Freeze ${f.name}`}
        onChange={(e) => patch(i, { freeze: e.target.checked })} />) },
    { field: 'symbol', header: <span className="qcb-symhead-status">Symbol</span>, headerText: 'Symbol', width: 64, sortable: false, filterable: false,
      text: ({ f }) => f.symbol ?? '',
      render: ({ f, i }) => (
        <input key={`${f.name}:${f.symbol ?? ''}`} className="qcb-cell qcb-symbol" defaultValue={f.symbol ?? ''} maxLength={12}
          title="Unique symbol used in formulas" disabled={readOnly} aria-label={`Symbol of ${f.name}`}
          onBlur={(e) => { commitSymbol(i, e.target.value); e.target.value = fields[i]?.symbol ?? ''; }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />) },
    { field: 'calc', header: <span className="qcb-symhead-status">Calc</span>, headerText: 'Calc', width: 52, sortable: false, filterable: false,
      align: 'center', text: ({ f }) => f.calc ?? '',
      render: ({ f, i }) => (
        <button type="button" className={`qcb-fxbtn${f.calc ? ' set' : ''}`} title={f.calc ? `${f.symbol} = ${f.calc}` : 'Add calculation'}
          onClick={() => (f.symbol ? onOpenFormula(i, 'calc') : notify.warning(`Assign a Symbol to ${labelOf(f)} before adding a formula.`))}>ƒx</button>) },
    { field: 'accept', header: <span className="qcb-symhead-status">Accept</span>, headerText: 'Accept', width: 64, sortable: false, filterable: false,
      align: 'center', text: ({ f }) => f.accept ?? '',
      render: ({ f, i }) => (
        <button type="button" className={`qcb-fxbtn${f.accept ? ' set' : ''}`} title={f.accept ? `Pass when ${f.accept}` : 'Add acceptance rule'}
          onClick={() => (f.symbol ? onOpenFormula(i, 'accept') : notify.warning(`Assign a Symbol to ${labelOf(f)} before adding a formula.`))}>✓</button>) },
    { field: 'move', header: 'Move', width: 56, sortable: false, filterable: false, noExport: true, align: 'center',
      render: ({ i }) => (
        <span className="qcb-move">
          <button type="button" title="Move up" aria-label="Move up" disabled={readOnly || i === 0} onClick={() => move(i, -1)}>↑</button>
          <button type="button" title="Move down" aria-label="Move down" disabled={readOnly || i === fields.length - 1} onClick={() => move(i, 1)}>↓</button>
        </span>) },
  ];

  return (
    <div className="qcb-fields">
      <div className="qcb-pane">
        <div className="qcb-fieldhead"><span>Available Fields</span><span>({avail.length})</span></div>
        <input className="qcb-pane-search" placeholder="Search available fields..." value={aSearch}
          data-unsaved-ignore onChange={(e) => setASearch(e.target.value)} />
        <div className="bl-card qcb-grid">
          <AppDataGrid<AvailableField> rows={availShown} columns={availCols} getRowId={(a) => a.oid}
            defaultPageSize={ALL_ROWS} hideHeaderTools maximizable={false} autoFocusFirstRow={false} hideFooter
            emptyText="No matching fields" />
          {/* The prototype's plain `.gridFooter` — a record count, no pager (every row is on one page). */}
          <div className="qcb-grid-footer"><span>Records: {avail.length}</span></div>
        </div>
      </div>
      <div className="qcb-pane">
        <div className="qcb-fieldhead"><span>Selected Fields</span><span>({fields.length})</span></div>
        <input className="qcb-pane-search" placeholder="Search selected fields..." value={sSearch}
          data-unsaved-ignore onChange={(e) => setSSearch(e.target.value)} />
        <div className="bl-card qcb-grid">
          <AppDataGrid<Row> rows={selShown} columns={selCols} getRowId={({ f }) => f.oid ?? `new:${f.name}`}
            defaultPageSize={ALL_ROWS} hideHeaderTools maximizable={false} autoFocusFirstRow={false} fixedLayout hideFooter
            emptyText="No fields selected. Add fields from the left." />
          <div className="qcb-grid-footer">
            <span>Records: {fields.length}</span><span className="qcb-note">Row order sets column order in the logbook.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
